import { type Nullable } from '@Types';
import { type InstitutionId } from 'Institutions';
import { statsigClientProvider } from 'StatsigClientProvider';
import { type ProgramType, ProgramTypeConfigs } from 'Program';
import {
    type ProgramList,
    type ProgramListItem,
    type ProgramListWithSubSections,
    type ProgramListWithSubSectionsItem,
    type SelectProgramConfig,
} from './selectProgram.types';

// Type guard to check if a section config has subSections.
// Helps prevent having to cast to ProgramListWithSubSections.
function hasSubSections(
    sectionConfig: ProgramList | ProgramListWithSubSections,
): sectionConfig is ProgramListWithSubSections {
    return 'subSections' in sectionConfig;
}

// Type guard to check if a program list item has a subSection
// Helps prevent having to cast to ProgramListWithSubSectionsItem.
function hasSubSection(item: ProgramListItem): item is ProgramListWithSubSectionsItem {
    return 'subSection' in item;
}

export const selectProgramConfig = async (institutionID: InstitutionId): Promise<Nullable<SelectProgramConfig>> => {
    const dynamicConfig = await statsigClientProvider.waitForDynamicConfig('select_program_config');
    if (!dynamicConfig) {
        return null;
    }

    return (dynamicConfig.value as { programTypes: ProgramType[] }).programTypes.reduce((acc, programType) => {
        const programTypeConfig = ProgramTypeConfigs[programType];
        if (!programTypeConfig || !programTypeConfig.selectProgramListItem) {
            return acc;
        }

        const selectProgramListItem = programTypeConfig.selectProgramListItem;
        if (!selectProgramListItem.isVisible(institutionID)) {
            return acc;
        }

        let sectionConfig = acc.find(
            (sc: ProgramList | ProgramListWithSubSections) => sc.section === selectProgramListItem.section,
        );
        if (!sectionConfig) {
            sectionConfig = {
                section: selectProgramListItem.section,
                className: `${selectProgramListItem.section}-programs`,
                title: { key: `settings.select_program.section_${selectProgramListItem.section}_programs` },
            } as ProgramList | ProgramListWithSubSections;

            if (hasSubSection(selectProgramListItem)) {
                sectionConfig.intro = {
                    key: `settings.select_program.section_${selectProgramListItem.section}_intro`,
                };
                (sectionConfig as ProgramListWithSubSections).subSections = [];
            } else {
                (sectionConfig as ProgramList).items = [];
            }
            acc.push(sectionConfig);
        }

        if (hasSubSections(sectionConfig)) {
            let subSectionConfig = sectionConfig.subSections.find(
                (ssc: ProgramList) =>
                    hasSubSection(selectProgramListItem) && ssc.subSection === selectProgramListItem.subSection,
            );
            if (!subSectionConfig && hasSubSection(selectProgramListItem)) {
                subSectionConfig = {
                    section: selectProgramListItem.section,
                    subSection: selectProgramListItem.subSection,
                    title: {
                        // section_rise_to_c_suite
                        // section_data_and_finance
                        // section_ai_and_future_of_business
                        key: `settings.select_program.section_${selectProgramListItem.subSection}`,
                    },
                    intro: {
                        // section_rise_to_c_suite_intro
                        // section_data_and_finance_intro
                        // section_ai_and_future_of_business_intro
                        key: `settings.select_program.section_${selectProgramListItem.subSection}_intro`,
                    },
                    items: [],
                };
                sectionConfig.subSections.push(subSectionConfig);
            }
            subSectionConfig?.items.push(selectProgramListItem);
        } else {
            sectionConfig.items.push(selectProgramListItem);
        }

        return acc;
    }, [] as SelectProgramConfig);
};
